import type { PreparedPortableText } from '../vector/portable-text-outlines';
import type { PortableTextLayoutInput, PortableTextLayoutArtifacts } from '../vector/portable-text-layout';
import type { BidiRequest, BidiPlan } from '../../../packages/text-bidi/browser/index';
import type { MaipoShapeTextRunsInput } from '../../../packages/text-shaping/src/index';
import { throwIfAborted } from '../codecs/types';

export interface PortableTextArtifacts { plan: string; outlines: string }
const MAX_FONT_BYTES = 64 * 1024 * 1024;
const MAX_ACTIVE = 2;
let active = 0;

/** Exact-font, explicitly segmented text. Each operation owns its worker so
 * cancellation interrupts synchronous Wasm and releases that engine's memory.
 * Paragraph/run resolution and native text layout adoption are separate layers.
 */
export function shapePortableText(input: MaipoShapeTextRunsInput, signal?: AbortSignal): Promise<PortableTextArtifacts> {
  try {
    throwIfAborted(signal);
    if (active >= MAX_ACTIVE) throw new Error('Portable text is busy. Finish or cancel a pending operation.');
    if (!(input.fontBytes instanceof Uint8Array) || !(input.fontBytes.buffer instanceof ArrayBuffer)
      || input.fontBytes.byteLength === 0 || input.fontBytes.byteLength > MAX_FONT_BYTES) throw new Error('Portable text font bytes exceed the supported limit.');
    if (typeof input.text !== 'string' || input.text.length > 32_768 || Array.from(input.text).length > 16_384 || !Array.isArray(input.runs) || input.runs.length > 1024) throw new Error('Portable text input exceeds the supported limit.');
    const { fontBytes, ...settings } = input;
    const captured = { ...structuredClone(settings), fontBytes: new Uint8Array(fontBytes) };
    return startPortableText({ operation: 'shape', input: captured }, [captured.fontBytes.buffer], signal).then(value => {
      const result = value as Partial<PortableTextArtifacts> | null;
      if (typeof result?.plan !== 'string' || typeof result.outlines !== 'string') throw new Error('Invalid shaping result.');
      return { plan: result.plan, outlines: result.outlines };
    });
  } catch (error) { return Promise.reject(error instanceof Error ? error : new Error('Could not prepare portable text input.')); }
}

/** Resolve paragraph levels and visual scalar order using the pinned Unicode engine. */
export async function resolvePortableTextDirection(input: BidiRequest, signal?: AbortSignal): Promise<BidiPlan> {
  throwIfAborted(signal);
  if (typeof input.text !== 'string' || input.text.length > 32_768 || Array.from(input.text).length > 16_384) throw new Error('Portable text input exceeds the supported limit.');
  if (input.lineEnds && (!Array.isArray(input.lineEnds) || input.lineEnds.length > 16_384)) throw new Error('Portable text line count exceeds the supported limit.');
  const result = await startPortableText({ operation: 'bidi', input: structuredClone(input) }, [], signal);
  if (!result || typeof result !== 'object' || !('lines' in result) || !Array.isArray(result.lines)) throw new Error('Invalid direction result.');
  return result as BidiPlan;
}

/** Exact-font paragraph layout; keeps the draft record separate from native documents. */
async function runPortableLayout(input: PortableTextLayoutInput, operation: 'layout' | 'prepare-layout', signal?: AbortSignal): Promise<unknown> {
  throwIfAborted(signal);
  if (typeof input.text !== 'string' || input.text.length > 20_000 || Array.from(input.text).length > 10_000) throw new Error('Portable layout exceeds the source limit.');
  if (!Array.isArray(input.fonts) || input.fonts.length === 0 || input.fonts.length > 16) throw new Error('Portable layout exceeds the font count limit.');
  let bytes = 0;
  for (const font of input.fonts) {
    if (!(font.fontBytes instanceof Uint8Array) || !(font.fontBytes.buffer instanceof ArrayBuffer) || font.fontBytes.byteLength === 0 || font.fontBytes.byteLength > MAX_FONT_BYTES) throw new Error('Portable layout font exceeds the byte limit.');
    bytes += font.fontBytes.byteLength;
  }
  if (bytes > 128 * 1024 * 1024) throw new Error('Portable layout exceeds the total font byte limit.');
  if (active >= MAX_ACTIVE) throw new Error('Portable text is busy. Finish or cancel a pending operation.');
  const { fonts, ...settings } = input;
  const captured = { ...structuredClone(settings), fonts: fonts.map(({ fontBytes, ...font }) => ({ ...structuredClone(font), fontBytes: new Uint8Array(fontBytes) })) };
  const transfer = [...new Set(captured.fonts.map(font => font.fontBytes.buffer))];
  return startPortableText({ operation, input: captured }, transfer, signal);
}

export async function layoutPortableText(input: PortableTextLayoutInput, signal?: AbortSignal): Promise<PortableTextLayoutArtifacts> {
  const value = await runPortableLayout(input, 'layout', signal);
  const result = value as Partial<PortableTextLayoutArtifacts> | null;
  if (typeof result?.serialized !== 'string' || typeof result.digest !== 'string') throw new Error('Invalid layout result.');
  return { serialized: result.serialized, digest: result.digest };
}

export async function preparePortableText(input: PortableTextLayoutInput, signal?: AbortSignal): Promise<PreparedPortableText> {
  const value = await runPortableLayout(input, 'prepare-layout', signal);
  const result = value as Partial<PreparedPortableText> | null;
  if (!result?.layout || typeof result.layout.serialized !== 'string' || typeof result.layout.digest !== 'string'
    || !result.geometry || !Array.isArray(result.geometry.paths) || !Array.isArray(result.geometry.glyphs) || !Array.isArray(result.geometry.lines) || !Array.isArray(result.geometry.clusters) || !Array.isArray(result.geometry.insertionLines)) throw new Error('Invalid prepared text result.');
  return { layout: result.layout, geometry: result.geometry };
}

function startPortableText(message: unknown, transfer: Transferable[], signal?: AbortSignal): Promise<unknown> {
  if (active >= MAX_ACTIVE) return Promise.reject(new Error('Portable text is busy. Finish or cancel a pending operation.'));
  return new Promise((resolve, reject) => {
    let worker: Worker;
    try { worker = new Worker(new URL('./portable-text.worker.ts', import.meta.url), { type: 'module', name: 'maipo-portable-text' }); }
    catch (error) { reject(error instanceof Error ? error : new Error('Could not create portable text worker.')); return; }
    active++;
    let settled = false;
    const finish = (error?: Error, result?: unknown) => {
      if (settled) return;
      settled = true; clearTimeout(timer); signal?.removeEventListener('abort', abort);
      worker.terminate(); active--;
      if (error) reject(error); else resolve(result);
    };
    const abort = () => finish(new DOMException('Portable text cancelled.', 'AbortError'));
    const timer = setTimeout(() => finish(new Error('Portable text exceeded its 30-second work limit.')), 30_000);
    signal?.addEventListener('abort', abort, { once: true });
    worker.onerror = event => { event.preventDefault(); finish(new Error('Portable text worker could not run.')); };
    worker.onmessageerror = () => finish(new Error('Portable text worker returned an unreadable result.'));
    worker.onmessage = (event: MessageEvent<{ type?: string; result?: unknown; message?: string }>) => {
      if (event.data?.type === 'result' && event.data.result !== undefined) finish(undefined, event.data.result);
      else finish(new Error(typeof event.data?.message === 'string' ? event.data.message : 'Portable text worker returned an invalid result.'));
    };
    try { worker.postMessage(message, transfer); }
    catch (error) { finish(error instanceof Error ? error : new Error('Could not start portable text shaping.')); }
  });
}
